/**
 * Variant B: Compact Layout
 * 1 badge únic amb info essencial en una línia
 * Expandible per veure detalls
 * Ideal per mòbil - minimitza scroll
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCalendar, IconBed, IconUsers, IconBarcode, IconCheck, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { Badge } from '@/components/ui/badge';
import { calendarNights, formatCalendarDate } from '@/lib/dates';
import type { ReservationInfoVariantProps } from './types';

export function VariantB({ reserva, onInteraction }: ReservationInfoVariantProps) {
  const { t, i18n } = useTranslation('guestPortal');
  const [expanded, setExpanded] = useState(false);

  const lang = i18n.resolvedLanguage || i18n.language;
  const nights = calendarNights(reserva.checkIn, reserva.checkOut);

  const getStatusBadge = () => {
    const status = reserva.status || 'CONFIRMED';
    const statusMap: Record<string, { label: string; className: string }> = {
      CONFIRMED: { label: t('reservationInfo.confirmed'), className: 'bg-green-100 text-green-700' },
      PENDING: { label: t('reservationInfo.pending'), className: 'bg-yellow-100 text-yellow-700' },
      CHECKED_IN: { label: t('reservationInfo.checkedIn'), className: 'bg-blue-100 text-blue-700' },
      CHECKED_OUT: { label: t('reservationInfo.checkedOut'), className: 'bg-gray-100 text-gray-700' },
    };
    const statusInfo = statusMap[status] || statusMap.CONFIRMED;
    return <Badge className={`${statusInfo.className} text-xs px-2 py-0.5`}>{statusInfo.label}</Badge>;
  };

  const handleToggle = () => {
    setExpanded(!expanded);
    onInteraction?.(expanded ? 'collapse' : 'expand');
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl shadow-md overflow-hidden">
      {/* Compact header - Always visible */}
      <div 
        className="p-4 cursor-pointer active:bg-blue-100/50 transition-colors"
        onClick={handleToggle}
      >
        {/* Row 1: Name + Status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <IconCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span className="font-semibold text-gray-900 text-sm truncate">
              {reserva.guest_name}
            </span>
          </div>
          {getStatusBadge()}
        </div>
        
        {/* Row 2: All info in one line */}
        <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
          <span className="flex items-center gap-1 bg-white/60 px-2 py-1 rounded-full">
            <IconCalendar className="h-3 w-3" />
            {formatCalendarDate(reserva.checkIn, lang, 'd MMM')} → {formatCalendarDate(reserva.checkOut, lang, 'd MMM')}
          </span>
          <span className="flex items-center gap-1 bg-white/60 px-2 py-1 rounded-full">
            <IconBed className="h-3 w-3" />
            {reserva.room?.name || 'N/A'}
          </span>
          <span className="flex items-center gap-1 bg-white/60 px-2 py-1 rounded-full">
            <IconUsers className="h-3 w-3" />
            {reserva.numAdults + reserva.numChildren}
          </span>
          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
            {t('reservationInfo.nights', { count: nights })}
          </span>
        </div>

        {/* Expand indicator */}
        <div className="flex justify-center mt-2">
          {expanded ? (
            <IconChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <IconChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded details - Collapsible */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-200/50 animate-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-2 gap-3 mt-3">
            {/* Check-in */}
            <div className="bg-white rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">{t('reservationInfo.checkIn')}</p>
              <p className="text-sm font-medium text-gray-900">{formatCalendarDate(reserva.checkIn, lang)}</p>
            </div>
            {/* Check-out */}
            <div className="bg-white rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">{t('reservationInfo.checkOut')}</p>
              <p className="text-sm font-medium text-gray-900">{formatCalendarDate(reserva.checkOut, lang)}</p>
            </div>
          </div>

          {/* Room details */}
          {reserva.room?.type && (
            <div className="mt-3 bg-white rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">{t('reservationInfo.room')}</p>
              <p className="text-sm font-medium text-gray-900">{reserva.room.name}</p>
              <p className="text-xs text-gray-500">{reserva.room.type}</p>
            </div>
          )}

          {/* Booking code */}
          {reserva.beds24Id && (
            <div className="mt-3 bg-white rounded-xl p-3 flex items-center justify-between">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <IconBarcode className="h-3 w-3" />
                {t('reservationInfo.bookingCode')}
              </span>
              <span className="font-mono text-sm font-medium text-gray-900">{reserva.beds24Id}</span>
            </div>
          )}

          {/* Welcome message - condensed */}
          <div className="mt-3 bg-green-50 rounded-xl p-3">
            <p className="text-sm text-green-800">
              <span className="font-medium">{t('welcome')}, {reserva.guest_name}!</span>
              {' '}{t('welcomeMessage')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
